import { useCallback } from 'react';

import { FilterValues } from '@/hooks/use-filter-values';
import { useFeatureFlags } from '@/modules/common/hooks';
import { PaginatedResponse } from '@/modules/common/types/pagination-types';
import { FcmNotificationType } from '@/modules/notifications';
import { useAreNotificationsEnabled, useFcmListRefresh } from '@/modules/notifications/hooks';

import { usePollingListRefresh } from './use-polling-list-refresh';

interface UseFinanceListRefreshArgs<TItem extends { id: number | string }> {
  /** FCM notification type for this list (e.g. INVOICE_READY / BILLS_READY). */
  notificationType: FcmNotificationType;
  /** TanStack Query key prefix for the infinite list (e.g. [INVOICES_QUERY_KEY]). */
  queryKey: unknown[];
  /** Applied filters — scopes the polling peek cache and resets its baseline. */
  filters: FilterValues['values'];
  /** Items currently shown (from the authoritative list query). */
  items: TItem[] | undefined;
  /** `isFetching` from the list query — used to hide the banner after refresh. */
  isFetching: boolean;
  /**
   * Whether the list currently has at least one item whose status can still
   * change server-side (e.g. in-progress). Polling only runs when true.
   */
  hasInProgressItem: boolean;
  /** Fetches the whole currently-loaded set in one request at the given size. */
  fetchAll: (pageSize: number) => Promise<PaginatedResponse<TItem>>;
  /** Structural hash of the items (id + status is enough for the list view). */
  hashItems: (items: TItem[] | undefined) => string;
}

interface UseFinanceListRefreshResult {
  /** True when the banner should show (FCM push arrived or polling found changes). */
  hasPendingRefresh: boolean;
  /** Reloads the list to page 1 (the shared FCM/polling reload path). */
  refresh: () => void;
}

/**
 * Unified refresh signal for the invoices/bills list pages.
 *
 * Encapsulates the FCM-vs-polling decision behind the `useFcmForFinanceLists`
 * feature flag so both list pages share one implementation:
 *
 * - Flag OFF (default): FCM is ignored entirely and long-polling is the sole
 *   refresh path — it runs whenever the list has an in-progress item. Disabled
 *   by default while backend FCM delivery is unreliable (OPS-1560 / OPS-1449).
 * - Flag ON: FCM push drives the banner and polling reverts to a fallback that
 *   only runs when push notifications are unavailable.
 *
 * Both `useFcmListRefresh` and `usePollingListRefresh` are always called (Rules
 * of Hooks); the flag gates their *outputs* and the polling `enabled` flag, so
 * the FCM subscription is inert (a no-op handler that never fires) when off.
 */
export const useFinanceListRefresh = <TItem extends { id: number | string }>({
  notificationType,
  queryKey,
  filters,
  items,
  isFetching,
  hasInProgressItem,
  fetchAll,
  hashItems,
}: UseFinanceListRefreshArgs<TItem>): UseFinanceListRefreshResult => {
  const { useFcmForFinanceLists } = useFeatureFlags();

  const { hasPendingRefresh: hasFcmRefresh, refresh: fcmRefresh } = useFcmListRefresh({
    notificationType,
    queryKey,
    isFetching,
  });

  const areNotificationsEnabled = useAreNotificationsEnabled();
  // Flag OFF → polling is the only path (always poll when in-progress items exist).
  // Flag ON → polling is a fallback, active only when push notifications aren't.
  const shouldPoll = useFcmForFinanceLists ? !areNotificationsEnabled : true;

  const { hasPendingRefresh: hasPollingRefresh, refresh: pollingRefresh } =
    usePollingListRefresh<TItem>({
      queryKey,
      filters,
      items,
      loadedCount: items?.length ?? 0,
      isFetching,
      enabled: shouldPoll && hasInProgressItem,
      fetchAll,
      hashItems,
    });

  const hasPendingRefresh = (useFcmForFinanceLists && hasFcmRefresh) || hasPollingRefresh;

  const refresh = useCallback(() => {
    if (useFcmForFinanceLists) fcmRefresh();
    pollingRefresh();
  }, [useFcmForFinanceLists, fcmRefresh, pollingRefresh]);

  return { hasPendingRefresh, refresh };
};
